import React, { useState } from 'react';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { COMPANY } from '../constants';
import { buildWhatsAppUrl } from '../utils';
import styles from './ContactPage.module.css';

function WaIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

const CONTACT_ITEMS = [
  { icon: '📞', label: 'Phone / WhatsApp', value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
  { icon: '✉️', label: 'Email', value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  { icon: '📍', label: 'Address', value: COMPANY.address, href: COMPANY.mapLink },
  { icon: '🕐', label: 'Business Hours', value: COMPANY.hours, href: null },
];

function RequirementForm() {
  const [type, setType]       = useState('Buying');
  const [title, setTitle]     = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent]       = useState(false);
  const [errors, setErrors]   = useState({});

  const validate = () => {
    const e = {};
    if (!title.trim())   e.title   = 'Please enter a title.';
    if (!message.trim()) e.message = 'Please enter your message.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSend = () => {
    if (!validate()) return;
    const text =
      `*New ${type} Requirement – Bright Machineries*\n\n` +
      `*Type:* ${type}\n` +
      `*Title:* ${title.trim()}\n\n` +
      `*Details:*\n${message.trim()}`;
    const url = buildWhatsAppUrl(text);
    window.open(url, '_blank', 'noopener,noreferrer');
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setTitle('');
      setMessage('');
      setErrors({});
    }, 4000);
  };

  return (
    <div className={styles.reqForm}>
      <div className={styles.reqFormHeader}>
        <span className={styles.reqFormIcon}>📋</span>
        <div>
          <h3 className={styles.reqFormTitle}>Post a Requirement</h3>
          <p className={styles.reqFormSub}>Looking to buy or sell? Send us the details via WhatsApp.</p>
        </div>
      </div>

      {/* Type toggle */}
      <div className={styles.typeToggle}>
        {['Buying', 'Selling'].map(opt => (
          <button
            key={opt}
            className={`${styles.typeBtn} ${type === opt ? styles.typeBtnActive : ''}`}
            onClick={() => setType(opt)}
          >
            {opt === 'Buying' ? '🛒' : '🏷️'} {opt}
          </button>
        ))}
      </div>

      {/* Title */}
      <div className={styles.reqField}>
        <label className={styles.reqLabel}>
          {type === 'Buying' ? 'Machine you need' : 'Machine you want to sell'}
          <span className={styles.reqRequired}>*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={e => { setTitle(e.target.value); setErrors(v => ({ ...v, title: '' })); }}
          placeholder={type === 'Buying' ? 'e.g. CNC Lathe, 500mm swing, 2015 or newer' : 'e.g. Fanuc Robodrill D21, 2019'}
          className={`${styles.reqInput} ${errors.title ? styles.reqInputError : ''}`}
        />
        {errors.title && <span className={styles.reqError}>{errors.title}</span>}
      </div>

      {/* Message */}
      <div className={styles.reqField}>
        <label className={styles.reqLabel}>
          Additional details
          <span className={styles.reqRequired}>*</span>
        </label>
        <textarea
          value={message}
          onChange={e => { setMessage(e.target.value); setErrors(v => ({ ...v, message: '' })); }}
          placeholder={
            type === 'Buying'
              ? 'Budget, preferred brand, location, urgency, any other specs…'
              : 'Condition, year, asking price, location, reason for sale…'
          }
          className={`${styles.reqTextarea} ${errors.message ? styles.reqInputError : ''}`}
          rows={4}
        />
        {errors.message && <span className={styles.reqError}>{errors.message}</span>}
      </div>

      {sent ? (
        <div className={styles.sentMsg}>
          ✅ WhatsApp opened! Your requirement was sent in a new tab.
        </div>
      ) : (
        <button className={styles.reqSendBtn} onClick={handleSend}>
          <WaIcon size={17} /> Send via WhatsApp
        </button>
      )}
    </div>
  );
}

export default function ContactPage() {
  const { openGeneral } = useWhatsApp();
  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.heroStripe} />
        <div className="container">
          <div className={styles.heroBadge}>Contact Us</div>
          <h1 className={styles.heroTitle}>GET IN <span className={styles.yellow}>TOUCH</span></h1>
          <p className={styles.heroSub}>We're available Mon–Sat, 9 AM to 6 PM. WhatsApp responses within minutes.</p>
        </div>
      </div>

      <div className="container">
        <div className={styles.grid}>
          {/* Left column: contact info + requirement form */}
          <div className={styles.leftCol}>
            <div className={styles.contactCards}>
              {CONTACT_ITEMS.map(item => (
                <div key={item.label} className={styles.contactCard}>
                  <span className={styles.contactIcon}>{item.icon}</span>
                  <div>
                    <div className={styles.contactLabel}>{item.label}</div>
                    {item.href
                      ? <a href={item.href} className={styles.contactValue} target="_blank" rel="noreferrer">{item.value}</a>
                      : <div className={styles.contactValue}>{item.value}</div>
                    }
                  </div>
                </div>
              ))}
            </div>

            <RequirementForm />
          </div>

          {/* Right column: quick CTA panel */}
          <div className={styles.ctaPanel}>
            <h2 className={styles.ctaTitle}>Quickest Ways to Reach Us</h2>
            <p className={styles.ctaSub}>For buying, selling, or any machine enquiry, WhatsApp or a quick call is the fastest way to connect with us.</p>
            <div className={styles.ctaBtns}>
              <a href={`tel:${COMPANY.phone}`} className={styles.callBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                Call Now
              </a>
              <button className={styles.waBtn} onClick={openGeneral}>
                <WaIcon /> WhatsApp Us
              </button>
              <a href={`mailto:${COMPANY.email}`} className={styles.emailBtn}>
                ✉️ Send Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
